import { useMemo } from "react"
import { useCohortPermissionsQuery } from "../api/generated"
import {
  hasEditorLevelAccess,
  hasLearnerLevelAccess,
  hasModeratorLevelAccess,
  hasObserverLevelAccess,
  hasTeacherLevelAccess
} from "../group/permissions"
import { usePathwrightContext } from "../pathwright/PathwrightContext"
import { canAdministrate } from "../user/permissions"

const useCohortPermissions = ({ cohortId }: { cohortId?: number | null }) => {
  const pwContext = usePathwrightContext()
  const cohortPermissionsQuery = useCohortPermissionsQuery({
    variables: {
      cohortId: cohortId!
    },
    skip: !cohortId || canAdministrate(pwContext),
    notifyOnNetworkStatusChange: true
  })

  const cohort = cohortPermissionsQuery.data?.cohort
  const sourceCohort = cohort?.source || cohort

  return useMemo(() => {
    const hasAdminLevelAccess = canAdministrate(pwContext)
    // NOTE: a value of null indicates to the consumer that the permission
    // query is still loading
    const defaultPerm = cohortPermissionsQuery.loading ? null : false

    return {
      hasEditorLevelAccess:
        hasAdminLevelAccess ||
        hasEditorLevelAccess(pwContext, cohort || sourceCohort) ||
        defaultPerm,
      hasTeacherLevelAccess:
        hasAdminLevelAccess ||
        hasTeacherLevelAccess(pwContext, cohort || sourceCohort) ||
        defaultPerm,
      hasModeratorLevelAccess:
        hasAdminLevelAccess ||
        hasModeratorLevelAccess(pwContext, cohort || sourceCohort) ||
        defaultPerm,
      hasLearnerLevelAccess:
        hasAdminLevelAccess ||
        hasLearnerLevelAccess(pwContext, cohort || sourceCohort) ||
        defaultPerm,
      hasObserverLevelAccess:
        hasAdminLevelAccess ||
        hasObserverLevelAccess(pwContext, cohort || sourceCohort) ||
        defaultPerm
    }
  }, [cohortPermissionsQuery.loading])
}

export default useCohortPermissions
